import {
  Box,
  Dialog,
  Stack,
  SwipeableDrawer,
  Typography
} from "@mui/material";
import {
  alpha,
  styled,
  useTheme
} from "@mui/material/styles";
import {
  PickersDay,
  PickersDayProps
} from "@mui/x-date-pickers";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { Icon } from "../../components";
import { ButtonText } from "../../components/Button";
import { ButtonDigited } from "../Button/Button.variants";

export const CalendarContainerStack = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  minHeight: "26.375rem",
  position: "relative",

  [theme.breakpoints.down("lg")]: {
    margin: "0",
    minHeight: "auto",
    width: "100%"
  },

  ".MuiDateCalendar-root": {
    overflow: "visible",
    color: theme.customColors.exploreCardText,
    margin: "0 0 0 auto",
    maxHeight: "100%",

    [theme.breakpoints.down("lg")]: {
      width: "100%",
      margin: "0"
    }
  },

  "& .MuiDateCalendar-root .MuiPickersCalendarHeader-labelContainer": {
    "button": {
      display: "none !important"
    },
    "div": {
      fontSize: "1.5rem",
      fontWeight: "700",

      [theme.breakpoints.down("sm")]: {
        fontSize: "1rem",
        lineHeight: "1.3125rem",
        textTransform: "capitalize"
      }
    }
  },

  ".MuiPickersCalendarHeader-root": {
    margin: "0",
    marginBottom: "1.2rem",
    padding: "0",
    position: "relative",
    textTransform: "capitalize",

    [theme.breakpoints.down("sm")]: {
      marginBottom: "16px",
      maxHeight: "20px"
    }
  },

  ".MuiPickersCalendarHeader-labelContainer": {
    display: "block",
    position: "absolute",
    textAlign: "center",
    width: "100%"
  },

  ".MuiPickersCalendarHeader-label": {
    margin: 0
  },

  ".MuiPickersCalendarHeader-switchViewButton": {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    opacity: 1,
    pointerEvents: "auto"
  },

  ".MuiPickersArrowSwitcher-root": {
    gap: "4rem",
    justifyContent: "space-around",
    width: "100%"
  },

  "& .MuiPickersArrowSwitcher-root button.Mui-focusVisible": {
    outline: `2px solid ${theme.customColors.exploreCardText}`,
    outlineOffset: "2px",
    borderRadius: "2px"
  },

  ".MuiDayCalendar-weekDayLabel": {
    color: theme.customColors.systemSecondary01,
    fontSize: "1rem",
    fontWeight: "700",
    height: "1.5625rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem"
    }
  },

  ".MuiDayCalendar-slideTransition": {
    minHeight: "350px"
  },

  ".MuiYearCalendar-root": {
    width: "100%"
  },

  ".MuiDayCalendar-monthContainer": {
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
    padding: theme.spacing(0.8, 0, 1.5, 0),

    [theme.breakpoints.down("sm")]: {
      paddingBottom: "0px"
    }
  },

  ".MuiDayCalendar-header": {
    justifyContent: "space-around",
    padding: theme.spacing(0, 1)
  },

  ".MuiDayCalendar-weekContainer": {
    justifyContent: "space-around",
    padding: theme.spacing(0, 1),

    [theme.breakpoints.down("sm")]: {
      alignItems: "center",
      gap: "0",
      height: "40px",
      margin: "0",
      padding: "0"
    }
  },
  "& #calendar-switch-view-btn": {
    display: "inline-flex !important",
    "& svg": {
      opacity: 0             // ← but hide the icon inside
    },
    "&:focus-visible": {
      opacity: 1,              // ← show the button on focus
      outline: `2px solid ${theme.customColors.exploreCardText}`,
      outlineOffset: "-2px",
      borderRadius: "2px"
    }
  }
}));

export const CourseStack = styled(Stack)(() => ({
  overflow: "hidden",
  whiteSpace: "nowrap"
}));

export const CurrentDayOfWeekTypography = styled(Typography)(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  fontSize: "1.5rem",
  fontWeight: "400",
  lineHeight: "1.8125rem"
}));

export const CurrentDayTypography = styled(Typography)(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  fontSize: "4rem",
  fontWeight: "700",
  lineHeight: "4.875rem",
  textAlign: "center"
}));

export const SelectedDayTypography = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "0.875rem",
  fontWeight: "400",
  lineHeight: "2.0625rem"
}));

export const CourseTypography = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "0.75rem",
  fontWeight: "400",
  lineHeight: "0.75rem",
  padding: "0.125rem",
  whiteSpace: "nowrap"
}));

export const CalendarArrowBox = styled(Box)(({ theme }) => ({
  display: "flex",
  "& svg:hover": {
    filter: `drop-shadow(0px 0px 2px ${theme.customColors.exploreCardText})`
  },
  "& path": {
    fill: theme.customColors.exploreCardText
  }
}));

export function CalendarArrow({ icon, iconGlow, description }: { icon: string, iconGlow: string, description: string }) {
  const [showingIcon, setShowingIcon] = useState<string>(icon);

  return (
    <CalendarArrowBox aria-label={ description }>
      <Icon
        icon={ showingIcon }
        width="2rem"
        onMouseDown={ () => setTimeout(() => setShowingIcon(iconGlow), 1) }
        onTouchStart={ () => setShowingIcon(iconGlow) }
        onMouseUp={ () => setTimeout(() => setShowingIcon(icon), 1) }
        onTouchEnd={ () => setShowingIcon(icon) }
      />
    </CalendarArrowBox>
  );
}

export const OpenCalendarArrowBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOpen"
})<{ isOpen: boolean }>(({ isOpen }) => ({
  alignSelf: "center",
  cursor: "pointer",
  display: "flex",
  right: "16px",
  top: "10px",
  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"
}));

export function OpenCalendarArrow({ isOpen, ...props }: { isOpen: boolean }) {
  const theme = useTheme();

  return (
    <OpenCalendarArrowBox isOpen={ isOpen } { ...props }>
      <ChevronDown size="1.25rem" color={ theme.customColors.backgroundPrimaryCta }/>
    </OpenCalendarArrowBox>
  );
}

export const StyledPickersDay = styled(PickersDay<Date>, {
  shouldForwardProp: (prop) => prop !== "activityColor" && prop !== "$sameDay" && prop !== "$selected"
})<
  PickersDayProps<Date> & {
    activityColor: {
      backgroundColor: string,
      textColor: string
    },
    $sameDay: boolean,
    $selected: boolean
  }
>(({
  activityColor,
  $sameDay,
  $selected,
  theme
}) => ({
  aspectRatio: $selected ? "1" : undefined,
  backgroundColor: $selected ? theme.customColors.systemSecondary01 : "none",
  border: "2px solid transparent",

  color: $selected
    ? activityColor.textColor
    : $sameDay
      ? theme.customColors.systemSecondary01
      : theme.customColors.exploreCardText,
  fontSize: "1rem",
  fontWeight: "700",
  height: "2.5rem",
  width: "2.5rem",

  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    height: "1.5625rem",
    lineHeight: "1.5076rem",
    width: "1.5625rem"
  },

  "&.Mui-selected": {
    backgroundColor: theme.customColors.systemSecondary01
  },

  "&:focus,&:focus-visible": {
    backgroundColor: $selected ? theme.customColors.systemSecondary01 : `rgba(${theme.customColors.backgroundSecondaryHover})`
  },

  "&:hover,&:focus-visible": {
    backgroundColor: $selected ? theme.customColors.systemSecondary01 : `rgba(${theme.customColors.backgroundSecondaryHover})`,

    [`${DotSpan}`]: {
      transition: "max-height 50ms ease-in, max-width 50ms ease-out"
      // maxHeight: 0,
      // maxWidth: 0
    }
  },

  "&:active": {
    aspectRatio: "1",
    background: $selected
      ? theme.customColors.systemSecondary01
      : alpha(activityColor.backgroundColor, 0.25)
  }
}));

export const DotSpan = styled("span")(({ theme }) => ({
  backgroundColor: theme.customColors.accentVariantA,
  borderRadius: "50%",
  bottom: "0.25rem",
  position: "absolute",
  transition: "max-height 450ms ease-in, max-width 450ms ease-in",

  height: "0.3125rem",
  maxHeight: "1rem",
  maxWidth: "1rem",
  width: "0.3125rem",

  [theme.breakpoints.down("sm")]: {
    bottom: "-6px",
    maxHeight: "4px",
    maxWidth: "4px"
  }
}));

export const DaySpan = styled("span")(() => ({
  lineHeight: "1.5625rem"
}));

export const SmartLearningButton = styled(ButtonText)(({ theme }) => ({
  backdropFilter: "blur(2px)",
  backgroundColor: theme.customColors.backgroundPrimary
    && alpha(theme.customColors.backgroundPrimary, 0.4),
  border: `0.5px solid ${theme.customColors.borderTag}`,
  borderRadius: "8px",
  bottom: 0,
  // boxShadow: "4px 4px 12px 0px #15151540",
  justifyContent: "flex-start",
  marginBottom: "24px",
  minWidth: "57px",
  overflow: "hidden",
  padding: "16px",
  position: "absolute",
  transition: "all .3s linear",
  width: "57px",

  [theme.breakpoints.down("sm")]: {
    marginBottom: "0",
    position: "relative"
  },

  "&:hover,&:focus-visible": {
    backgroundColor: theme.customColors.backgroundPrimary
      && alpha(theme.customColors.backgroundPrimary, 0.4),
    width: "320px"
  }
}));

export const SmartLearningCreateButton = styled(ButtonDigited)(({ theme }) => ({
  "&.Mui-focusVisible": {
    outline: `2px solid ${theme.customColors.borderPrimaryCta}`,
    outlineOffset: "2px",
    borderRadius: "2px"
  }
}));

export const CalendarSmartDrawer = styled(SwipeableDrawer)(({ theme }) => ({
  "& .MuiModal-backdrop": {
    bottom: "60px",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100% - 120px)"
    }
  },
  "& .MuiPaper-root": {
    background: theme.customColors.backgroundCard,
    border: `2px solid ${theme.customColors.border}`,
    "&:hover": {
      border: `2px solid ${theme.customColors.borderAccent}`
    },
    borderColor: theme.customColors.borderAccent,
    borderRadius: "8px 8px 0 0",
    bottom: "60px",
    boxShadow: `0 0 4px 0 ${theme.customColors.borderAccent}`,
    padding: "16px 20px",
    transition: "none",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100% - 120px)"
    }
  }
}));

export const CalendarSmartDialog = styled(Dialog)(({ theme }) => ({
  ".MuiDialog-paper": {
    background: theme.customColors.backgroundCard,
    border: `0.125rem solid ${theme.customColors.border}`,
    "&:hover": {
      border: `0.125rem solid ${theme.customColors.borderAccent}`
    },
    borderRadius: "1rem",
    bottom: "10%",
    padding: "1.5rem"
    // width: "494px"
  }
}));

export const LabelTypography = styled(Typography)(({ theme }) => ({
  color: theme.customColors.textPrimary,
  fontSize: "24px",
  fontWeight: "700",
  lineHeight: "30px",
  whiteSpace: "nowrap"
}));

export const FormLabelTypography = styled(Typography)(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  fontSize: "0.874rem",
  fontWeight: "400",
  lineHeight: "1.25rem",
  width: "fit-content"
}));

export const FormSubLabelTypography = styled(Typography)(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  fontSize: "12px",
  fontWeight: "400",
  lineHeight: "1rem"
}));

export const AvailableHoursTypography = styled(Typography)(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  fontSize: "0.875rem",
  fontWeight: "400",
  lineHeight: "1.25rem",
  minHeight: "21px"
}));

export const TitleTypography = styled(Typography)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  fontSize: "1.5rem",
  fontStyle: "normal",
  fontWeight: "700",
  lineHeight: "1.8rem",

  [theme.breakpoints.down("sm")]: {
    fontSize: "1.25rem",
    lineHeight: "1.5rem"
  }
}));

export const DescriptionTypography = styled(Typography)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  fontSize: "0.875rem",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "1rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    lineHeight: "0.875rem"
  }
}));
